import React, { useMemo, useState } from 'react';
import EditButton from '../../ui/EditButton';
import './documents.css';
import {
  getDocumentActionLabels,
  getDocumentSourceUrl,
  getVisibleDocuments,
  isJoinRequirementDocument,
  normalizeTeamDocuments,
  triggerDocumentDownload,
} from '../utils/documentUtils';

function DocumentBadge({ children }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.15rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: 'rgba(15, 23, 42, 0.08)',
        color: '#334155',
      }}
    >
      {children}
    </span>
  );
}

export default function DocumentsCard({ documents = [], isAdmin = false, isMember = false, onEdit }) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const normalizedDocuments = useMemo(() => normalizeTeamDocuments(documents), [documents]);
  const visibleDocuments = useMemo(() => (
    isAdmin
      ? normalizedDocuments
      : getVisibleDocuments(normalizedDocuments, { isAdmin: false, isMember })
  ), [normalizedDocuments, isAdmin, isMember]);
  const joinRequirementCount = useMemo(() => (
    normalizedDocuments.filter(doc => doc.active && isJoinRequirementDocument(doc)).length
  ), [normalizedDocuments]);
  const filteredDocuments = useMemo(() => visibleDocuments.filter((document) => {
    const term = search.trim().toLowerCase();
    if (term && !`${document.title} ${document.description} ${document.eventGroupLabel}`.toLowerCase().includes(term)) return false;
    if (categoryFilter !== 'all' && document.category !== categoryFilter) return false;
    if (visibilityFilter !== 'all' && document.visibility !== visibilityFilter) return false;
    return true;
  }), [visibleDocuments, search, categoryFilter, visibilityFilter]);

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Documents</span>
        {isAdmin && <EditButton section="documents" onEdit={onEdit} />}
      </div>
      <div className="card-body">
        {visibleDocuments.length === 0 ? (
          <p className="text-muted">
            {isAdmin
              ? 'No documents added yet. Add useful files, policies, or required join documents.'
              : 'No documents available right now.'}
          </p>
        ) : (
          <>
            {isAdmin && (
              <>
                <p className="text-muted" style={{ marginBottom: '0.75rem' }}>
                  {normalizedDocuments.length} document{normalizedDocuments.length !== 1 ? 's' : ''} total,{' '}
                  {joinRequirementCount} required on join.
                </p>
                <div className="documents-card__filters">
                  <input type="search" className="input documents-card__search" aria-label="Search documents" placeholder="Search documents or linked event" value={search} onChange={event => setSearch(event.target.value)} />
                  <select className="input" aria-label="Filter document category" value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)}><option value="all">All categories</option><option value="reference">Reference</option><option value="join_requirement">Required on join</option></select>
                  <select className="input" aria-label="Filter document visibility" value={visibilityFilter} onChange={event => setVisibilityFilter(event.target.value)}><option value="all">All visibility</option><option value="public">Public</option><option value="members">Members only</option><option value="confirmed_bookers">Confirmed bookers</option></select>
                </div>
              </>
            )}

            <div style={{ display: 'grid', gap: '0.9rem' }}>
              {filteredDocuments.length === 0 && (
                <p className="text-muted" role="status">No documents match your filters.</p>
              )}
              {filteredDocuments.map(doc => {
                const sourceUrl = getDocumentSourceUrl(doc);
                const actionLabels = getDocumentActionLabels(doc);
                const content = (
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div style={{ fontWeight: 700, lineHeight: 1.5 }}>{doc.title || 'Untitled document'}</div>
                    {doc.description && (
                      <p className="text-muted" style={{ marginTop: '0.35rem', marginBottom: 0 }}>
                        {doc.description}
                      </p>
                    )}
                  </div>
                );

                return (
                  <div
                    key={doc.id}
                    style={{
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: '14px',
                      padding: '0.9rem 1rem',
                      background: doc.active ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 252, 0.7)',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gap: '0.75rem',
                      }}
                    >
                      {sourceUrl ? (
                        <a
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            minWidth: 0,
                          }}
                        >
                          {content}
                        </a>
                      ) : (
                        <div style={{ minWidth: 0 }}>
                          {content}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {sourceUrl && (
                          <>
                            <a className="btn btn--secondary btn--xs" href={sourceUrl} target="_blank" rel="noopener noreferrer">
                              {actionLabels.view}
                            </a>
                            <button type="button" className="btn btn--secondary btn--xs" onClick={() => triggerDocumentDownload(doc)}>
                              {actionLabels.download}
                            </button>
                          </>
                        )}
                        {isJoinRequirementDocument(doc) && <DocumentBadge>Required on join</DocumentBadge>}
                        <DocumentBadge>{doc.visibility === 'members' ? 'Members only' : doc.visibility === 'confirmed_bookers' ? 'Confirmed bookers' : 'Public'}</DocumentBadge>
                        {doc.versionLabel && <DocumentBadge>{doc.versionLabel}</DocumentBadge>}
                        {!doc.active && <DocumentBadge>Inactive</DocumentBadge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
